import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import RequestPasswordResetModal from "../../components/auth/RequestPasswordResetModal";
import LoginForm from "../../components/auth/LoginForm/LoginForm";
import { useAuth } from "../../hooks/useAuth";
import { ApiError } from "../../lib/api";
import { apiErrorToMessage } from "../../utils/ApiUtils";
import { getAuthenticatedRedirectPath, getSafeRedirectPath } from "../../utils/AuthUtils";
import type { LoginRequestDto } from "../../types/types";

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, login, status, user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isPasswordResetModalOpen, setIsPasswordResetModalOpen] = useState(false);
  const requestedRedirectPath = getSafeRedirectPath(searchParams.get("redirect"));
  const redirectPath = getAuthenticatedRedirectPath(user?.roles, requestedRedirectPath);
  const defaultEmail = searchParams.get("email") ?? "";

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectPath]);

  const handleLogin = async (payload: LoginRequestDto) => {
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const authUser = await login(payload);
      navigate(getAuthenticatedRedirectPath(authUser.roles, requestedRedirectPath), { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setErrorMessage("Invalid email or password.");
      } else if (error instanceof ApiError && error.status === 403) {
        setErrorMessage(apiErrorToMessage(error, "This account cannot be accessed."));
      } else {
        setErrorMessage("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (status === "idle" || status === "loading") {
    return (
      <main className="mx-auto max-w-md px-6 py-16">
        <p className="text-myteengig-text-muted">Checking your session...</p>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-md px-6 py-16">
      <RequestPasswordResetModal
        isOpen={isPasswordResetModalOpen}
        onClose={() => setIsPasswordResetModalOpen(false)}
        defaultEmail={defaultEmail}
      />
      <h1 className="mb-6 text-2xl font-bold text-myteengig-text-primary">Log in</h1>
      <LoginForm
        onSubmit={handleLogin}
        isSubmitting={isSubmitting}
        errorMessage={errorMessage}
        defaultEmail={defaultEmail}
        footer={
          <div className="flex items-center justify-between gap-4 text-sm text-myteengig-text-muted">
            <button
              type="button"
              onClick={() => setIsPasswordResetModalOpen(true)}
              className="font-medium text-myteengig-primary hover:underline"
            >
              Forgot your password?
            </button>
            <Link to="/signup" className="font-medium text-myteengig-primary hover:underline">
              Sign up
            </Link>
          </div>
        }
      />
      <p className="mt-4 text-center text-sm text-myteengig-text-muted">
        <Link to="/" className="font-medium text-myteengig-primary hover:underline">
          Back to Home
        </Link>
      </p>
    </main>
  );
};

export default LoginPage;
